import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/emergency/fire-preparation')({
  component: FirePreparationPage,
  head: () => ({
    meta: [
      { title: 'Bushfire preparation for WA south-west — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          "Plain-English bushfire preparation checklist for WA's south-west: BAL ratings, DFES warning levels, what to pack, when to leave, and links to each shire's local emergency management plan.",
      },
      { property: 'og:title', content: 'Bushfire preparation — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          "What a Watch and Act actually means, what to keep in your car, when to leave, and your shire's local plan.",
      },
    ],
  }),
});

const SHIRE_LEMP_LINKS: { id: string; name: string; lempUrl: string }[] = [
  {
    id: 'augusta-margaret-river',
    name: 'Shire of Augusta-Margaret River',
    lempUrl: 'https://www.amrshire.wa.gov.au/services/emergency-management',
  },
  {
    id: 'nannup',
    name: 'Shire of Nannup',
    lempUrl: 'https://www.nannup.wa.gov.au/our-shire/emergency-management.aspx',
  },
  {
    id: 'manjimup',
    name: 'Shire of Manjimup',
    lempUrl: 'https://www.manjimup.wa.gov.au/our-services/emergency-management',
  },
  {
    id: 'bridgetown-greenbushes',
    name: 'Shire of Bridgetown-Greenbushes',
    lempUrl: 'https://www.bridgetown.wa.gov.au/our-services/emergency-management.aspx',
  },
  {
    id: 'boyup-brook',
    name: 'Shire of Boyup Brook',
    lempUrl: 'https://www.boyupbrook.wa.gov.au/our-shire/emergency-management.aspx',
  },
  {
    id: 'denmark',
    name: 'Shire of Denmark',
    lempUrl: 'https://www.denmark.wa.gov.au/services/emergency-services-and-management.aspx',
  },
];

function FirePreparationPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.5rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-xl)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
    letterSpacing: '0.02em',
  };
  const h3: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.1rem',
    color: 'var(--ink)',
    marginTop: 'var(--sp-md)',
    marginBottom: '0.4rem',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.6,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = {
    color: 'var(--green-mid)',
    fontWeight: 600,
  };
  const card = (bg: string, border: string): React.CSSProperties => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-sm)',
  });

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Emergency · Evergreen guide
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Are you bushfire ready?
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        Warren-Blackwood covers 18,280 km² of bushfire-prone karri, jarrah and
        marri forest. Most years, somewhere in the electorate will be on Watch
        and Act between December and April. This page is the plain-English
        version of the DFES <em>Are You Bushfire Ready?</em> framework — what
        the warning levels mean, what to pack, and when to leave.
      </p>

      <div
        style={{
          ...card('#fff7e6', '#f0c674'),
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
        }}
      >
        <strong style={{ color: '#8a5a00' }}>In an emergency right now</strong>
        <p style={{ ...p, margin: 0 }}>
          Life-threatening emergency: <strong>call 000</strong>.<br />
          Current warnings and incidents:{' '}
          <a
            href="https://www.emergency.wa.gov.au/"
            style={link}
          >
            emergency.wa.gov.au
          </a>{' '}
          or DFES information line 13 DFES (<a href="tel:133337" style={link}>13 3337</a>).
        </p>
      </div>

      <h2 style={h2}>Step 1 — Write your bushfire plan today</h2>
      <p style={p}>
        DFES's free <strong>My Bushfire Plan</strong> tool walks you through
        the decisions you don't want to make at 2pm on a 40-degree day with
        smoke on the horizon: will you leave early, where will you go, who
        picks up the kids, what about the animals, who do you call.
      </p>
      <p style={p}>
        <a
          href="https://www.mybushfireplan.wa.gov.au/"
          style={link}
        >
          Start your plan at mybushfireplan.wa.gov.au →
        </a>
      </p>
      <p style={p}>
        Print a copy. Stick it on the fridge. Make sure every adult in the house
        has read it. A plan that lives only in your head is not a plan.
      </p>

      <h2 style={h2}>Step 2 — Know what the warning levels mean</h2>
      <p style={p}>
        DFES uses three nationally-consistent levels. Treat each one as an
        action, not just an update.
      </p>

      <div style={card('#eefaf0', '#bfe5c6')}>
        <h3 style={{ ...h3, color: 'var(--rivergum)', marginTop: 0 }}>
          Advice — a fire has started, no immediate danger
        </h3>
        <p style={{ ...p, marginBottom: 0 }}>
          <strong>Do:</strong> Stay informed. Check{' '}
          <a href="https://www.emergency.wa.gov.au/" style={link}>
            emergency.wa.gov.au
          </a>{' '}
          regularly. Know where the fire is in relation to you. Tell vulnerable
          neighbours.
        </p>
      </div>

      <div style={card('#fff4e1', '#e8b870')}>
        <h3 style={{ ...h3, color: '#9a5a00', marginTop: 0 }}>
          Watch and Act — there is a possible threat to lives or homes
        </h3>
        <p style={{ ...p, marginBottom: 0 }}>
          <strong>Do:</strong> Leave now if your plan is to leave. Conditions
          can change fast. If you wait until you can see flames, the roads may
          already be closed or full of smoke. This is the level at which most
          people who die in bushfires decide too late.
        </p>
      </div>

      <div style={card('#fdecec', '#e09494')}>
        <h3 style={{ ...h3, color: '#a02020', marginTop: 0 }}>
          Emergency Warning — you are in danger and need to act immediately
        </h3>
        <p style={{ ...p, marginBottom: 0 }}>
          <strong>Do:</strong> Follow the instructions in the warning exactly.
          If it's safe to leave, leave by the route DFES recommends — not the
          one you usually take. If it's no longer safe to leave, shelter inside
          a solid building away from windows and call 000 if your life is in
          danger.
        </p>
      </div>

      <h2 style={h2}>Step 3 — Know your BAL (Bushfire Attack Level)</h2>
      <p style={p}>
        BAL is a rating of how much bushfire risk a particular property faces,
        based on vegetation, slope, and distance to fuel. Most of
        Warren-Blackwood sits in a designated bushfire-prone area, which means
        new builds and major renovations have to be built to a minimum BAL
        standard.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>BAL-LOW</strong> — insufficient risk to warrant specific
          construction requirements.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>BAL-12.5</strong> — ember attack.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>BAL-19</strong> — increasing ember attack and burning debris,
          plus some radiant heat.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>BAL-29</strong> — increasing radiant heat with embers.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>BAL-40</strong> — high radiant heat, embers, and increased
          likelihood of flame contact.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>BAL-FZ (Flame Zone)</strong> — direct flame contact likely.
          The highest rating.
        </li>
      </ul>
      <p style={p}>
        If you don't know your property's BAL, ask your shire's building
        services team or check the planning report you were given at purchase.
        BAL-29 and above means your home was built to resist a bushfire — it
        doesn't mean you should stay during one.
      </p>

      <h2 style={h2}>Step 4 — Pack a relocation kit you can grab in 5 minutes</h2>
      <p style={p}>Keep this together, somewhere you can reach without thinking:</p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          Wallet, phone, charger, car keys, house keys.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Photo ID, Medicare card, insurance details (photo on phone is fine).
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          A week of any prescription medication, plus scripts.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Long cotton or wool clothing, sturdy closed-in boots, leather gloves,
          a broad-brim hat, P2 mask, safety goggles.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          At least 3 litres of drinking water per person, plus a water bottle in
          the car.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Battery or wind-up radio (mobile networks fail in fires — ABC South
          West WA on 684 AM is the official emergency broadcaster for our
          region).
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Torch, spare batteries, basic first aid kit.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          A wool blanket per person (synthetic blankets melt — wool doesn't).
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Pet carrier, lead, food and water bowl for every animal you're
          taking. Decide in advance which animals you can't take, and what
          happens to them.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Irreplaceables: a USB with photos, birth certificates, the kids'
          favourite toy. One small box.
        </li>
      </ul>

      <h2 style={h2}>Step 5 — Keep your car ready</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          Keep the fuel tank above half during summer. Service stations close
          when the power goes out.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Park facing out, with the keys in the ignition or somewhere
          everyone knows.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Wool blanket per person in the boot, plus a 5-litre jerry of drinking
          water.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          A paper map of the south-west. You will lose mobile reception.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Know two routes out of your area. Roads close in fires — your usual
          way home may not be available.
        </li>
      </ul>

      <h2 style={h2}>Step 6 — Decide when to leave (and leave then)</h2>
      <p style={p}>
        The single decision that saves lives in bushfires is leaving early.
        "Early" means before a Watch and Act, not during one. If you've decided
        your plan is to leave, the trigger should be a <strong>Catastrophic</strong>{' '}
        or <strong>Extreme</strong> Fire Danger Rating in your district —
        check{' '}
        <a href="https://www.emergency.wa.gov.au/" style={link}>
          emergency.wa.gov.au
        </a>{' '}
        each morning during summer.
      </p>
      <p style={p}>
        On those days, by mid-morning, you should already be where you intend
        to be. Don't wait to see what happens. The risk isn't the fire you can
        see — it's the one that starts behind you while you're driving.
      </p>

      <h2 style={h2}>Your shire's local emergency management plan</h2>
      <p style={p}>
        Every shire in Warren-Blackwood has a Local Emergency Management
        Arrangements (LEMA) document covering evacuation centres, vulnerable
        residents, and the local incident management structure. Read your
        shire's plan once, before fire season, so you know where the evacuation
        centre is.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem', listStyle: 'none' }}>
        {SHIRE_LEMP_LINKS.map((s) => (
          <li key={s.id} style={{ marginBottom: '0.6rem' }}>
            <a href={s.lempUrl} style={link}>
              {s.name} — emergency management page
            </a>
            <br />
            <Link
              to="/shire/$name"
              params={{ name: s.id }}
              style={{ ...link, fontSize: '0.9rem', fontWeight: 500 }}
            >
              See council contacts and services on this site →
            </Link>
          </li>
        ))}
      </ul>

      <h2 style={h2}>Trusted sources</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.emergency.wa.gov.au/" style={link}>
            emergency.wa.gov.au
          </a>{' '}
          — live warnings and incident map.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.mybushfireplan.wa.gov.au/" style={link}>
            mybushfireplan.wa.gov.au
          </a>{' '}
          — the official DFES bushfire planning tool.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.dfes.wa.gov.au/hazard-information/bushfire" style={link}>
            dfes.wa.gov.au/hazard-information/bushfire
          </a>{' '}
          — Are You Bushfire Ready? guides, including translated and
          easy-read versions.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          ABC South West WA — <strong>684 AM</strong> — the official emergency
          broadcaster for our region when networks fail.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          DFES information line 13 DFES — <a href="tel:133337" style={link}>13 3337</a>.
        </li>
      </ul>

      <p
        style={{
          marginTop: 'var(--sp-lg)',
          fontSize: '0.78rem',
          color: 'var(--ink-light)',
          borderTop: '1px solid var(--rule)',
          paddingTop: 'var(--sp-sm)',
          lineHeight: 1.5,
        }}
      >
        This page is a community summary of publicly-available DFES guidance.
        It is not an official emergency communication. In an active incident,
        always follow the instructions on{' '}
        <a href="https://www.emergency.wa.gov.au/" style={link}>
          emergency.wa.gov.au
        </a>{' '}
        and from emergency services. Last reviewed: June 2026.
      </p>
    </main>
  );
}
